import { Box, FormControl, SelectChangeEvent, Typography } from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useEffect, useRef, useState } from "react";
import styles from "../devotee.module.scss";
import constants from "../../../config/constants.json";
import {
  apiRequest,
  catchErrorMessageHandler,
  getUserDetails,
  getUserName,
} from "../../../infrastructure/backendService";
import {
  CustomSelect,
  CustomMenuItem,
  CommonButton,
} from "../../common/StyledComponents/StyledFields";
import toast from "react-hot-toast";
import apiPath from "../../../config/apiPaths.json";
import { DonationReportResponseProps } from "../../../infrastructure/GlobalContextProvider";
import Sign_Chairman from "../../../assets/RYSignature3.png";
import ErrorPopup from "../../common/ErrorPopup";
import President from "../../../assets/President.png";
import { useReactToPrint } from "react-to-print";

interface DonationReportProps {
  year: string;
}

const DonationReport = (props: DonationReportProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const currentDate = new Date();
  const [itemsData, setItemsData] = useState<DonationReportResponseProps[]>([]);
  const [userDetails, setUserdetails] = useState({
    address: "",
    city: "",
    zipcode: "",
    state: "",
    country: "",
  });
  const formattedDate = currentDate.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const [tanAmount, setTanAmount] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [buttondisable, setButtonDisable] = useState<boolean>(true);
  const [chairmanPresidentNames, setChairmanPresidentNames] = useState([]);

  const getYear = (): string => {
    if (props.year === "Year End Report") {
      return `${currentDate.getFullYear() - 1}`;
    } else if (props.year === "Donation Report") {
      return `${currentDate.getFullYear()}`;
    } else {
      return props.year;
    }
  };

  useEffect(() => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.donationReport,
      params: {
        yearOfDonations: getYear(),
      },
      includeToken: true,
    })
      .then((res) => {
        setItemsData(res.data.donations);
        setTanAmount(res.data.totalTangibleAmount);
        setYear(res.data.year);
        const total = res.data.donations.reduce(
          (accumulator: number, item: DonationReportResponseProps) => {
            // Extract numerical value from the amount string and convert to a number
            const amount = parseFloat(item.amount.replace(/[^\d.-]/g, ""));
            return accumulator + amount;
          },
          0
        );
        setTotalAmount(
          total.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            style: "currency",
            currency: "USD",
          })
        );
        setButtonDisable(false);
      })
      .catch((err) => {
        setItemsData([]);
        setTotalAmount(0);
        setYear(getYear());
        setButtonDisable(true);
        console.log(err);
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  }, [getYear()]);

  useEffect(() => {
    apiRequest({
      method: "GET",
      endpoint: `${apiPath.getDevoteeDetails}/${getUserName()?.toUpperCase()}`,
      includeToken: true,
    })
      .then((res) => {
        // setItemsData(res.data);
        setUserdetails({
          address: res.data.address,
          city: res.data.city,
          zipcode: res.data.zipcode,
          state: res.data.state,
          country: res.data.country,
        });
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  }, []);

  useEffect(() => {
    apiRequest({
      method: "GET",
      endpoint: "/temple-management/public/findChairmanAndPresidentNames",
      includeToken: false,
    })
      .then((res) => {
        console.log(res.data);
        setChairmanPresidentNames(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const Years = [
    {
      year: 2021,
    },
    {
      year: 2022,
    },
    {
      year: 2023,
    },
    {
      year: 2024,
    },
    {
      year: 2025,
    },
  ];

  const handleYearChange = (event: SelectChangeEvent<unknown>) => {
    setSelectedYear(event.target.value as number);
  };

  const pdfRef = useRef<any>();

  const printStyles = `
  @media print {
    /* Set paper size to Tabloid (11x17 inches) */
    @page {
      size: tabloid;
      margin: 24px;
    }
  
    /* Set layout to Landscape */
    body {
      width: 100vw;
      position: absolute;
      left: 100;
    }
  
    /* Add more styles as needed for printing */
  }
  
`;

  const handlePrint = useReactToPrint({
    content: () => pdfRef.current,
    pageStyle:printStyles,
  });

  const [loader, setloader] = useState(false);

  const downloadPDF = () => {
    const yearToDownload = getYear();
    const input = pdfRef.current;
    setButtonDisable(true);

    if (!input) {
      console.error("pdfRef is not assigned to an HTML element");
      return;
    }

    void html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4", true);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;
      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );
      pdf.save(`report_${yearToDownload}.pdf`);
      setButtonDisable(false);
    });
  };

  const HeaderDonation = (): React.ReactElement => {
    return (
      <Box className={styles.Header}>
        <Box className={styles.ImageAndImageName}>
          <img src="/images/TempleOldLogo.png" className={styles.HeaderImage} />
          <Typography className={styles.TempleNameData}>
            Shiva -Vishnu Temple
          </Typography>
        </Box>

        <Box className={styles.TempleAddress}>
          <Typography className={styles.TempleAddressData}>
            Hindu Community and Cultural Center
            <br />A Registered Non-Profit Organization
            <br />
            Tax ID: 94-2427126, Inc: D0821589,
            <br /> 1232, Arrowhead Ave, Livermore, CA <br />
            94551 Tel: (925) 449-6255, Fax: (925) 455-0404.
          </Typography>
        </Box>
      </Box>
    );
  };

  const DonationDropdown = (): React.ReactElement => {
    return (
      <FormControl
        sx={{ paddingTop: "5px", paddingBottom: "24px", width: "405px" }}
      >
        <CustomSelect
          sx={{ color: "#000" }}
          IconComponent={KeyboardArrowDownIcon}
          defaultValue={currentYear.toString()}
          id="grouped-select"
          onChange={handleYearChange}
          value={selectedYear}
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1px solid #F05802",
                "& ul": {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: "auto",
                  minHeight: "auto",
                  maxHeight: "300px",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#F3C539",
                    borderRadius: "6px",
                  },
                },
              },
            },
          }}
        >
          {Years.map((item, i) => (
            <CustomMenuItem value={item.year} key={i}>
              {item.year}
            </CustomMenuItem>
          ))}
        </CustomSelect>
      </FormControl>
    );
  };

  return (
    <div className={styles.DonationReportLayout}>
      <Typography
        sx={{
          color: "#414141",
          fontFamily: "Lato",
          fontSize: "20px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "140%",
          paddingBottom: "11px",
        }}
      >
        Year End Report
      </Typography>

      {/* <Box className={styles.dropdown}>
            <CustomInputLabel>Year</CustomInputLabel>
            {DonationDropdown()}
         </Box> */}
      <div className={styles.DonationReportLayout} ref={pdfRef}>
        <Box className={styles.reportContent}>
          <Box>{HeaderDonation()}</Box>
          <br />
          <Typography className={styles.SignInName}>
            {formattedDate} <br />
            <br /> Devotee Sign-in Name: {getUserName()}
          </Typography>
          <Typography className={styles.SignInAddress}>
            {getUserDetails()}, {userDetails.address}
            <br />
            {userDetails.city}, {userDetails.state}, {userDetails.country} -{" "}
            {userDetails.zipcode}
          </Typography>

          <Typography className={styles.loggedInMember}>
            Dear {getUserName()},{" "}
          </Typography>
          <Typography className={styles.loogedMemberGratitude}>
            Thank you for your generous donation to Hindu Community and Cultural
            Center, Shiva-Vishnu Temple, Livermore, California for the year{" "}
            {year} :
          </Typography>
          {itemsData
            .slice(0, -1)
            .map((item: DonationReportResponseProps, index: number) => (
              <Box className={styles.paymentDetails} key={index}>
                <Typography className={styles.paymentDetailsData}>
                  {item.type}:
                </Typography>
                <Typography className={styles.paymentDetailsData}>
                  {item.amount}
                </Typography>
              </Box>
            ))}
          {itemsData.length > 0 ? (
            <Box className={styles.paymentDetails} sx={{ paddingTop: "5px" }}>
              <Typography>
                <b>{itemsData[itemsData.length - 1].type} :</b>
              </Typography>
              <Typography>
                <b>{itemsData[itemsData.length - 1].amount} </b>
              </Typography>
            </Box>
          ) : (
            ""
          )}

          <Typography className={styles.AboutData}>
            This information is a record of contributions you made to HCCC
            during {year} and tangible value of goods and services received by
            you in return for these contributions during the {year} was{" "}
            {tanAmount}.
          </Typography>

          <Typography className={styles.AboutData}>
            Please take a moment to review and verify the above amounts for
            accuracy. If you find any discrepancies, please contact us as soon
            as possible. Please indicate your Sign-in Name in your
            correspondence.
          </Typography>
          <Typography className={styles.AboutData}>
            We appreciate your patronage and look forward to your continued
            involvement with the Shiva-Vishnu Temple.
          </Typography>

          <Box className={styles.ClosingSignature}>
            {chairmanPresidentNames.map((item: any) => (
              <div key={item.username}>
                <img
                  src={item.username === "CHAIRMAN" ? Sign_Chairman : President}
                  style={{ height: "50px", width: "200px" }}
                  alt="Signature"
                />
                <Typography className={styles.SignatureData}>
                {`${item.fullName} , ${item.username.toLowerCase()}`}
                </Typography>
              </div>
            ))}
          </Box>
        </Box>
      </div>
      <Box className={styles.Button}>
        <CommonButton onClick={handlePrint} disabled={buttondisable}>
          {loader ? "Loading...." : "Print"}
        </CommonButton>
        <CommonButton
          onClick={downloadPDF}
          sx={{ marginLeft: "16px" }}
          disabled={buttondisable}
        >
          Download
        </CommonButton>
      </Box>
    </div>
  );
};

export default DonationReport;
